import { MAX_EVENTS_PER_BATCH } from './chunk-MDSD4D7F.mjs';
export { MAX_EVENTS_PER_BATCH, TRACKING_CONFIG } from './chunk-MDSD4D7F.mjs';
import { z } from 'zod';

var EVENT_TYPES = [
  "PAGE_VIEW",
  "PAGE_LEAVE",
  "SESSION_START",
  "SESSION_END",
  "SCROLL",
  "TIME_ON_PAGE",
  "CLICK",
  "MOUSE_LEAVE_WINDOW",
  "HEARTBEAT"
];
var eventTypeSchema = z.enum(EVENT_TYPES);
var eventPayloadSchema = z.object({
  /** Seconds the visitor has spent on the current page. */
  timeOnPage: z.number().int().min(0).max(86400).optional(),
  /** Deepest scroll depth reached on the current page, 0–100. */
  scrollPercentage: z.number().int().min(0).max(100).optional(),
  /** CSS selector of a clicked element. */
  selector: z.string().max(500).optional(),
  /** Trimmed text content of a clicked element. */
  text: z.string().max(200).optional(),
  /** Document title of the current page. */
  title: z.string().max(300).optional()
}).strip();
var eventInputSchema = z.object({
  type: eventTypeSchema,
  url: z.string().url().max(2048),
  referrer: z.string().max(2048).nullish(),
  /** Client clock. Kept alongside the server clock so drift stays visible. */
  occurredAt: z.string().datetime(),
  payload: eventPayloadSchema.default({})
});
var ingestEventsRequestSchema = z.object({
  visitorId: z.string().min(1).max(64),
  sessionId: z.string().min(1).max(64),
  events: z.array(eventInputSchema).min(1).max(MAX_EVENTS_PER_BATCH)
});
var CONDITION_FIELDS = [
  "URL",
  "PATH",
  "REFERRER",
  "EVENT_TYPE",
  "TIME_ON_PAGE",
  "SCROLL_PERCENTAGE",
  "INTENT_SCORE",
  "VISIT_COUNT",
  "PAGE_VIEW_COUNT"
];
var conditionFieldSchema = z.enum(CONDITION_FIELDS);
var CONDITION_OPERATORS = ["EQUALS", "CONTAINS", "GREATER_THAN", "LESS_THAN"];
var conditionOperatorSchema = z.enum(CONDITION_OPERATORS);
var LOGICAL_OPERATORS = ["AND", "OR"];
var logicalOperatorSchema = z.enum(LOGICAL_OPERATORS);
var RULE_ACTIONS = [
  "SEND_AI_MESSAGE",
  "SEND_TEMPLATE_MESSAGE",
  "NOTIFY_FOUNDER",
  "SHOW_POPUP"
];
var ruleActionSchema = z.enum(RULE_ACTIONS);
var popupConfigSchema = z.object({
  title: z.string().min(1).max(120),
  description: z.string().max(600).default(""),
  emailPlaceholder: z.string().max(80).default("you@company.com"),
  submitLabel: z.string().min(1).max(40).default("Send it to me"),
  successMessage: z.string().min(1).max(200).default("Thanks \u2014 you're on the list."),
  /** Light or dark modal, independent of the widget's own theme. */
  theme: z.enum(["light", "dark"]).default("light"),
  /** Optional header image shown across the top of the modal. */
  imageUrl: z.string().url().max(2048).nullish(),
  /** Optional accent (button/focus) colour; falls back to the widget theme. */
  accentColor: z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Enter a hex colour like #2563eb").nullish()
});
var TEMPLATE_VARIABLE_HELP = [
  { token: "{{websiteName}}", description: "Your project name" },
  { token: "{{currentPage}}", description: "The path they are on, e.g. /pricing" },
  { token: "{{currentUrl}}", description: "The full URL they are on" },
  { token: "{{intentScore}}", description: "Their intent score, 0\u2013100" },
  { token: "{{visitCount}}", description: "How many times they have visited" }
];
function renderMessageTemplate(template, variables) {
  const lookup = variables;
  return template.replace(/\{\{\s*([a-zA-Z]+)\s*\}\}/g, (_match, name) => {
    const value = lookup[name];
    return value === void 0 ? "" : String(value);
  });
}
var TEXT_OPERATORS = ["EQUALS", "CONTAINS"];
var NUMERIC_OPERATORS = ["EQUALS", "GREATER_THAN", "LESS_THAN"];
var CONDITION_FIELD_DEFINITIONS = {
  URL: {
    field: "URL",
    label: "Full URL",
    valueType: "string",
    operators: TEXT_OPERATORS,
    hint: "https://example.com/pricing?plan=pro"
  },
  PATH: {
    field: "PATH",
    label: "Page path",
    valueType: "string",
    operators: TEXT_OPERATORS,
    hint: "/pricing"
  },
  REFERRER: {
    field: "REFERRER",
    label: "Referrer",
    valueType: "string",
    operators: TEXT_OPERATORS,
    hint: "google.com"
  },
  EVENT_TYPE: {
    field: "EVENT_TYPE",
    label: "Event type",
    valueType: "enum",
    operators: ["EQUALS"],
    options: EVENT_TYPES,
    hint: "The event being evaluated"
  },
  TIME_ON_PAGE: {
    field: "TIME_ON_PAGE",
    label: "Time on page (seconds)",
    valueType: "number",
    operators: NUMERIC_OPERATORS,
    hint: "60"
  },
  SCROLL_PERCENTAGE: {
    field: "SCROLL_PERCENTAGE",
    label: "Scroll depth (%)",
    valueType: "number",
    operators: NUMERIC_OPERATORS,
    hint: "75"
  },
  INTENT_SCORE: {
    field: "INTENT_SCORE",
    label: "Intent score",
    valueType: "number",
    operators: NUMERIC_OPERATORS,
    hint: "50"
  },
  VISIT_COUNT: {
    field: "VISIT_COUNT",
    label: "Visit count",
    valueType: "number",
    operators: NUMERIC_OPERATORS,
    hint: "2"
  },
  PAGE_VIEW_COUNT: {
    field: "PAGE_VIEW_COUNT",
    label: "Page views this session",
    valueType: "number",
    operators: NUMERIC_OPERATORS,
    hint: "3"
  }
};
var OPERATOR_LABELS = {
  EQUALS: "equals",
  CONTAINS: "contains",
  GREATER_THAN: "is greater than",
  LESS_THAN: "is less than"
};
var MAX_CONDITION_DEPTH = 5;
var MAX_CONDITION_NODES = 50;
var conditionLeafSchema = z.object({
  type: z.literal("condition"),
  field: conditionFieldSchema,
  operator: conditionOperatorSchema,
  value: z.union([z.string().max(500), z.number()])
}).superRefine((leaf, ctx) => {
  const definition = CONDITION_FIELD_DEFINITIONS[leaf.field];
  if (!definition.operators.includes(leaf.operator)) {
    ctx.addIssue({
      code: "custom",
      path: ["operator"],
      message: `Operator ${leaf.operator} is not valid for field ${leaf.field}`
    });
  }
  if (definition.valueType === "number" && typeof leaf.value !== "number") {
    ctx.addIssue({
      code: "custom",
      path: ["value"],
      message: `Field ${leaf.field} expects a numeric value`
    });
  }
  if (definition.valueType === "enum") {
    const allowed = definition.options ?? [];
    if (typeof leaf.value !== "string" || !allowed.includes(leaf.value)) {
      ctx.addIssue({
        code: "custom",
        path: ["value"],
        message: `Field ${leaf.field} expects one of: ${allowed.join(", ")}`
      });
    }
  }
});
var conditionGroupSchema = z.lazy(
  () => z.object({
    type: z.literal("group"),
    operator: logicalOperatorSchema,
    children: z.array(conditionNodeSchema).min(1)
  })
);
var conditionNodeSchema = z.lazy(
  () => z.union([conditionLeafSchema, conditionGroupSchema])
);
function conditionDepth(node) {
  if (node.type === "condition") return 1;
  return 1 + Math.max(...node.children.map(conditionDepth));
}
function conditionNodeCount(node) {
  if (node.type === "condition") return 1;
  return 1 + node.children.reduce((sum, child) => sum + conditionNodeCount(child), 0);
}
var conditionTreeSchema = conditionNodeSchema.refine((node) => conditionDepth(node) <= MAX_CONDITION_DEPTH, {
  message: `Conditions may not nest deeper than ${MAX_CONDITION_DEPTH} levels`
}).refine((node) => conditionNodeCount(node) <= MAX_CONDITION_NODES, {
  message: `A rule may not contain more than ${MAX_CONDITION_NODES} conditions`
});
var DEFAULT_RULE_COOLDOWN_SECONDS = 3600;
var ruleFieldsSchema = z.object({
  name: z.string().min(1).max(120),
  conditions: conditionTreeSchema,
  action: ruleActionSchema,
  /** Extra steer handed to the model when this rule opens a conversation. */
  aiInstruction: z.string().max(1e3).nullish(),
  /** Exact message for a SEND_TEMPLATE_MESSAGE rule; supports `{{variables}}`. */
  messageTemplate: z.string().max(2e3).nullish(),
  /** Modal content for a SHOW_POPUP rule. */
  popupConfig: popupConfigSchema.nullish(),
  enabled: z.boolean().default(true),
  priority: z.number().int().min(0).max(1e3).default(0),
  cooldownSeconds: z.number().int().min(0).max(86400).default(DEFAULT_RULE_COOLDOWN_SECONDS),
  triggerOncePerSession: z.boolean().default(true)
});
var templateRequired = (rule) => rule.action !== "SEND_TEMPLATE_MESSAGE" || Boolean(rule.messageTemplate && rule.messageTemplate.trim().length > 0);
var TEMPLATE_REQUIRED_ISSUE = {
  path: ["messageTemplate"],
  message: "A set-message rule needs a message template"
};
var popupRequired = (rule) => rule.action !== "SHOW_POPUP" || Boolean(rule.popupConfig?.title?.trim());
var POPUP_REQUIRED_ISSUE = {
  path: ["popupConfig"],
  message: "A popup rule needs a title"
};
var createRuleSchema = ruleFieldsSchema.extend({ projectId: z.string().min(1) }).refine(templateRequired, TEMPLATE_REQUIRED_ISSUE).refine(popupRequired, POPUP_REQUIRED_ISSUE);
var updateRuleSchema = ruleFieldsSchema.partial().refine(templateRequired, TEMPLATE_REQUIRED_ISSUE).refine(popupRequired, POPUP_REQUIRED_ISSUE);

// src/scoring.ts
var INTENT_SCORE_MAX = 100;
var INTENT_TIME_THRESHOLD_SECONDS = 60;
var DEFAULT_INTENT_SCORE_CONFIG = {
  cap: INTENT_SCORE_MAX,
  signals: [
    {
      id: "PRICING_PAGE",
      label: "Viewed pricing",
      points: 25,
      kind: "PATH",
      pathMatchers: ["/pricing", "/plans", "/upgrade"]
    },
    {
      id: "DOCS_PAGE",
      label: "Viewed docs",
      points: 10,
      kind: "PATH",
      pathMatchers: ["/docs", "/documentation", "/guide"]
    },
    {
      id: "INTEGRATIONS_PAGE",
      label: "Viewed integrations",
      points: 10,
      kind: "PATH",
      pathMatchers: ["/integrations", "/integration"]
    },
    {
      id: "TIME_ON_SITE",
      label: "Spent over a minute on site",
      points: 15,
      kind: "TIME_ON_SITE",
      thresholdSeconds: INTENT_TIME_THRESHOLD_SECONDS
    },
    {
      id: "RETURNING_VISITOR",
      label: "Returning visitor",
      points: 20,
      kind: "RETURNING_VISITOR",
      minVisitCount: 2
    }
  ]
};
function pathMatches(visitedPaths, matchers) {
  return visitedPaths.some((visited) => {
    const normalised = visited.toLowerCase();
    return matchers.some((matcher) => normalised.startsWith(matcher.toLowerCase()));
  });
}
function isExcludedPath(path, excludedPaths) {
  const normalised = path.toLowerCase();
  return excludedPaths.some((prefix) => normalised.startsWith(prefix.toLowerCase()));
}
function signalFires(signal, signals, behaviouralAllowed) {
  switch (signal.kind) {
    case "TIME_ON_SITE":
      return behaviouralAllowed && signals.totalTimeOnSiteSeconds > (signal.thresholdSeconds ?? INTENT_TIME_THRESHOLD_SECONDS);
    case "RETURNING_VISITOR":
      return behaviouralAllowed && signals.visitCount >= (signal.minVisitCount ?? 2);
    case "PATH":
      return pathMatches(signals.visitedPaths, signal.pathMatchers ?? []);
    default:
      return false;
  }
}
function computeIntentScore(signals, config = DEFAULT_INTENT_SCORE_CONFIG) {
  const contributions = [];
  const excludedPaths = config.excludedPaths ?? [];
  const behaviouralAllowed = excludedPaths.length === 0 || signals.visitedPaths.some((path) => !isExcludedPath(path, excludedPaths));
  for (const signal of config.signals) {
    if (signalFires(signal, signals, behaviouralAllowed)) {
      contributions.push({ key: signal.id, label: signal.label, points: signal.points });
    }
  }
  const rawScore = contributions.reduce((sum, contribution) => sum + contribution.points, 0);
  const cap = config.cap > 0 ? config.cap : INTENT_SCORE_MAX;
  return { score: Math.min(rawScore, cap), rawScore, contributions };
}
function intentTier(score) {
  if (score >= 60) return "hot";
  if (score >= 30) return "warm";
  return "cold";
}
var MESSAGE_ROLES = ["VISITOR", "AI", "FOUNDER", "SYSTEM"];
var messageRoleSchema = z.enum(MESSAGE_ROLES);
var CONVERSATION_MODES = ["AI", "HUMAN"];
var CONVERSATION_STATUSES = ["OPEN", "CLOSED"];
var CONVERSATION_OUTCOMES = ["NONE", "WON", "LOST"];
var conversationOutcomeSchema = z.enum(CONVERSATION_OUTCOMES);
var WIDGET_MODES = ["AUTO", "MANUAL", "SILENT"];
var OUTREACH_REF_PARAM = "ic_ref";
var CAMPAIGN_REF_PARAM = "ic_ca";
var OUTREACH_PARAMS = [OUTREACH_REF_PARAM, CAMPAIGN_REF_PARAM];
var DEFAULT_WIDGET_THEME = {
  accentColor: "#111111",
  position: "bottom-right",
  greeting: "Hi there \u2014 anything I can help you with?",
  colorScheme: "auto",
  founderName: "",
  founderRole: "Founder",
  founderAvatarUrl: ""
};
var widgetThemeSchema = z.object({
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a 6-digit hex colour").default(DEFAULT_WIDGET_THEME.accentColor),
  position: z.enum(["bottom-right", "bottom-left"]).default(DEFAULT_WIDGET_THEME.position),
  greeting: z.string().min(1).max(200).default(DEFAULT_WIDGET_THEME.greeting),
  colorScheme: z.enum(["auto", "light", "dark"]).default(DEFAULT_WIDGET_THEME.colorScheme),
  founderName: z.string().max(80).default(""),
  founderRole: z.string().max(60).default("Founder"),
  // Either empty or an absolute URL — a relative path can't resolve on a
  // customer's own domain.
  founderAvatarUrl: z.union([z.string().url().max(500), z.literal("")]).default("")
});
var intentSignalConfigSchema = z.object({
  id: z.string().min(1).max(40).regex(/^[A-Z0-9_]+$/, "Use uppercase letters, numbers and underscores"),
  label: z.string().min(1).max(80),
  points: z.number().int().min(0).max(100),
  kind: z.enum(["PATH", "TIME_ON_SITE", "RETURNING_VISITOR"]),
  pathMatchers: z.array(z.string().min(1).max(200)).max(20).optional(),
  thresholdSeconds: z.number().int().min(0).max(86400).optional(),
  minVisitCount: z.number().int().min(1).max(1e3).optional()
});
var intentScoreConfigSchema = z.object({
  cap: z.number().int().min(1).max(1e3).default(100),
  signals: z.array(intentSignalConfigSchema).max(30),
  /** Path prefixes treated as noise: they never earn behavioural score. */
  excludedPaths: z.array(z.string().min(1).max(200)).max(50).optional()
});
var createProjectSchema = z.object({
  name: z.string().min(1).max(120),
  websiteUrl: z.string().url().max(500),
  /**
   * Origins permitted to call the widget API. Empty means "same origin as
   * `websiteUrl`", which is resolved server-side at creation time.
   */
  allowedOrigins: z.array(z.string().url().max(500)).max(10).default([])
});
var slackWebhookUrlSchema = z.string().url().max(500).refine(
  (value) => value.startsWith("https://hooks.slack.com/"),
  "Must be a Slack incoming webhook URL (https://hooks.slack.com/\u2026)"
);
var updateProjectSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  websiteUrl: z.string().url().max(500).optional(),
  allowedOrigins: z.array(z.string().url().max(500)).max(10).optional(),
  systemPrompt: z.string().max(4e3).nullish(),
  widgetTheme: widgetThemeSchema.partial().optional(),
  /** Null resets scoring to the built-in defaults. */
  intentScoreConfig: intentScoreConfigSchema.nullish(),
  /** Empty string or null clears the webhook and disables Slack. */
  slackWebhookUrl: z.union([slackWebhookUrlSchema, z.literal("")]).nullish(),
  slackNotifyHighIntent: z.boolean().optional(),
  slackNotifyInbound: z.boolean().optional(),
  slackNotifyOutbound: z.boolean().optional(),
  slackExcludeCountries: z.array(z.string().trim().min(1).max(60)).max(200).optional(),
  widgetMode: z.enum(WIDGET_MODES).optional()
});
var bootstrapRequestSchema = z.object({
  /** Absent on a visitor's very first page load; the server mints one. */
  anonymousId: z.string().min(1).max(64).nullish(),
  /** Absent when the previous session has expired. */
  sessionId: z.string().min(1).max(64).nullish(),
  url: z.string().url().max(2048),
  referrer: z.string().max(2048).nullish(),
  title: z.string().max(300).nullish()
});
var timeString = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use 24h HH:MM");
var updateAvailabilitySchema = z.object({
  mode: z.enum(["AUTO", "AVAILABLE", "AWAY"]).optional(),
  timezone: z.string().max(64).nullish(),
  businessDays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
  businessStart: timeString.optional(),
  businessEnd: timeString.optional()
});
var visitorChatRequestSchema = z.object({
  visitorId: z.string().min(1).max(64),
  sessionId: z.string().min(1).max(64),
  conversationId: z.string().min(1).max(64).nullish(),
  content: z.string().min(1).max(4e3)
});
var popupSubmitSchema = z.object({
  visitorId: z.string().min(1).max(64),
  sessionId: z.string().min(1).max(64),
  ruleId: z.string().min(1).max(64),
  email: z.string().email().max(200)
});
var founderReplySchema = z.object({
  content: z.string().min(1).max(4e3)
});
var startConversationSchema = z.object({
  visitorId: z.string().min(1),
  content: z.string().min(1).max(4e3)
});
var takeoverSchema = z.object({
  conversationId: z.string().min(1)
});
var setOutcomeSchema = z.object({
  outcome: conversationOutcomeSchema
});
var ANALYTICS_RANGES = ["7d", "30d", "90d", "all"];
var trafficQuerySchema = z.object({
  projectId: z.string().min(1),
  range: z.enum(ANALYTICS_RANGES).default("30d")
});
var sessionsQuerySchema = z.object({
  projectId: z.string().min(1),
  range: z.enum(ANALYTICS_RANGES).default("30d"),
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(50).default(20)
});
var campaignPerformanceQuerySchema = z.object({
  projectId: z.string().min(1)
});
var listVisitorsQuerySchema = z.object({
  projectId: z.string().min(1),
  online: z.coerce.boolean().optional(),
  minScore: z.coerce.number().int().min(0).max(100).optional(),
  /** Leads view: restrict to visitors who have shared an email. */
  hasEmail: z.enum(["true", "false"]).optional().transform((value) => value === "true"),
  /** Outreach view: restrict to visitors attributed to an outbound link (ic_ref). */
  outreach: z.enum(["true", "false"]).optional().transform((value) => value === "true"),
  /** Restrict to visitors attributed to a specific campaign (`ic_ca`). */
  campaign: z.string().min(1).optional(),
  /** Keep only visitors whose city/region/country includes this text. */
  locationContains: z.string().trim().max(100).optional(),
  /** Drop visitors whose city/region/country includes this text. */
  locationExcludes: z.string().trim().max(100).optional(),
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(50)
});
var listConversationsQuerySchema = z.object({
  projectId: z.string().min(1),
  status: z.enum(CONVERSATION_STATUSES).optional(),
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(50)
});
var createKnowledgeEntrySchema = z.object({
  projectId: z.string().min(1),
  title: z.string().min(1).max(160),
  content: z.string().min(1).max(8e3),
  enabled: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1e4).default(0)
});
var updateKnowledgeEntrySchema = z.object({
  title: z.string().min(1).max(160).optional(),
  content: z.string().min(1).max(8e3).optional(),
  enabled: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(1e4).optional()
});
var MAX_KNOWLEDGE_PROMPT_CHARS = 6e3;
var setApprovalSchema = z.object({
  approved: z.boolean()
});

// src/realtime.ts
var SOCKET_NAMESPACES = {
  widget: "/widget",
  dashboard: "/dashboard"
};
var socketRooms = {
  visitor: (visitorId) => `visitor:${visitorId}`,
  project: (projectId) => `project:${projectId}`,
  conversation: (conversationId) => `conversation:${conversationId}`
};
var REALTIME_EVENTS = [
  "visitor_connected",
  "visitor_updated",
  "chat_message",
  "conversation_updated",
  "typing",
  "takeover_started",
  "takeover_ended",
  "show_popup"
];

export { ANALYTICS_RANGES, CAMPAIGN_REF_PARAM, CONDITION_FIELDS, CONDITION_FIELD_DEFINITIONS, CONDITION_OPERATORS, CONVERSATION_MODES, CONVERSATION_OUTCOMES, CONVERSATION_STATUSES, DEFAULT_INTENT_SCORE_CONFIG, DEFAULT_RULE_COOLDOWN_SECONDS, DEFAULT_WIDGET_THEME, EVENT_TYPES, INTENT_SCORE_MAX, INTENT_TIME_THRESHOLD_SECONDS, LOGICAL_OPERATORS, MAX_CONDITION_DEPTH, MAX_CONDITION_NODES, MAX_KNOWLEDGE_PROMPT_CHARS, MESSAGE_ROLES, OPERATOR_LABELS, OUTREACH_PARAMS, OUTREACH_REF_PARAM, REALTIME_EVENTS, RULE_ACTIONS, SOCKET_NAMESPACES, TEMPLATE_VARIABLE_HELP, WIDGET_MODES, bootstrapRequestSchema, campaignPerformanceQuerySchema, computeIntentScore, conditionDepth, conditionFieldSchema, conditionNodeCount, conditionNodeSchema, conditionOperatorSchema, conditionTreeSchema, conversationOutcomeSchema, createKnowledgeEntrySchema, createProjectSchema, createRuleSchema, eventInputSchema, eventPayloadSchema, eventTypeSchema, founderReplySchema, ingestEventsRequestSchema, intentScoreConfigSchema, intentSignalConfigSchema, intentTier, isExcludedPath, listConversationsQuerySchema, listVisitorsQuerySchema, logicalOperatorSchema, messageRoleSchema, popupConfigSchema, popupSubmitSchema, renderMessageTemplate, ruleActionSchema, sessionsQuerySchema, setApprovalSchema, setOutcomeSchema, slackWebhookUrlSchema, socketRooms, startConversationSchema, takeoverSchema, trafficQuerySchema, updateAvailabilitySchema, updateKnowledgeEntrySchema, updateProjectSchema, updateRuleSchema, visitorChatRequestSchema, widgetThemeSchema };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map